// Camera decoding for the pallet finder (F09 UF-04). Pallet labels carry a
// GS1-128 / Code 128 barcode (no QR). Both decoder backends already read
// Code 128, so only the payload shape is pallet-specific.
import { createFrameDecoder, type BadgeFrameDecoder } from './badgeCameraDecode'
import { isValidSscc, normaliseSscc } from './sscc'

// A pallet or bin number as the finder accepts it: dash-joined segments with a
// digit somewhere (KF49-2026-04418, RBN-2026-00007) or a bare run of 3+ digits.
const NUMBER_SHAPE = /^(?:[A-Z0-9]+(?:-[A-Z0-9]+)+|\d{3,})$/

// A digit-only payload this long that is not an 18-digit SSCC is a GS1 trade
// item barcode, not a pallet: our labels print "(02)GTIN(37)count" and
// "(01)GTIN(13)date" beside the SSCC, and a native detector returns them as
// plain digits. Refuse them so the camera keeps scanning for the SSCC.
const TRADE_ITEM_MIN_DIGITS = 16

function ssccFrom(raw: string): string | null {
  const sscc = normaliseSscc(raw)
  // A third party's SSCC may fail the check digit; the finder looks those up too.
  return isValidSscc(sscc) || /^\d{18}$/.test(sscc) ? sscc : null
}

function idFrom(raw: string): string | null {
  const id = raw.toUpperCase()
  if (!/\d/.test(id) || !NUMBER_SHAPE.test(id)) return null
  if (/^\d+$/.test(id) && id.length >= TRADE_ITEM_MIN_DIGITS) return null
  return id
}

/**
 * Pure: the SSCC among the decoded texts, else the first pallet / bin number.
 * Every text is tried for an SSCC before any is tried as a number, so a GTIN
 * barcode seen first in the same frame cannot win over the SSCC beside it.
 */
export function firstPalletFromTexts(texts: readonly string[]): string | null {
  const raws = texts.map(t => t.trim()).filter(Boolean)
  for (const raw of raws) {
    const sscc = ssccFrom(raw)
    if (sscc) return sscc
  }
  for (const raw of raws) {
    const id = idFrom(raw)
    if (id) return id
  }
  return null
}

export const makePalletDecoder = (): Promise<BadgeFrameDecoder> =>
  createFrameDecoder(firstPalletFromTexts)
